import React, { useMemo } from 'react';
import cx from 'classnames';
import { CANCELED_SPE_STATUS, COMPLETE_SPE_STATUS, NON_PROTOCOL, SITUATIONAL, UNEXPECTED } from 'constants/encounter';
import { isEmpty } from 'lodash/lang';

import EncounterSegment from './EncounterSegment/EncounterSegment';
import CardHeaderLeftUnexpectedAppointment from './EncounterSegment/templates/card-headers/CardHeaderLeftUnexpectedAppointment';
import CardHeaderMiddleDefault from './EncounterSegment/templates/card-headers/CardHeaderMiddleDefault';
import HeaderReview from './EncounterSegment/templates/HeaderReview';
import HeaderUnexpected from './EncounterSegment/templates/HeaderUnexpected';
import NullComponent from './EncounterSegment/templates/NullComponent';

export default function EachEncounter({ encounter, refetchTabData }) {
  const { encounterType, sitePatientEncounter, encounterEvents, noAppointments } = encounter;
  const { numberOfCompletedRequiredFields, encounterStatus } = sitePatientEncounter || {};
  const cancelled =
    encounter.sitePatientEncounter || encounter.projectedEncounterInfo
      ? encounterStatus === CANCELED_SPE_STATUS
      : encounter.encounterEvents?.every(event => event.cancelled);

  const style = useMemo(
    function() {
      if (encounterStatus === CANCELED_SPE_STATUS) {
        return { '--encounter-status': 'gray' };
      }
      if (!numberOfCompletedRequiredFields && numberOfCompletedRequiredFields !== 0) {
        return { '--encounter-status': '#691E44' };
      }
      if (encounterStatus !== COMPLETE_SPE_STATUS) {
        return { '--encounter-status': '#FFC738' };
      }
      return { '--encounter-status': '#27AE60' };
    },
    [encounterStatus, numberOfCompletedRequiredFields]
  );

  const isUnexpectedAppointment = encounterType === UNEXPECTED && !sitePatientEncounter;
  const isSituational = encounterType === SITUATIONAL;
  const isNonProtocolAndUnexpected = [NON_PROTOCOL, UNEXPECTED].includes(encounterType) && isEmpty(encounterEvents);

  const isReview = ['PI_REVIEW', 'SM_REVIEW'].includes(sitePatientEncounter?.encounterStatus);

  const templates = useMemo(
    function() {
      if (isSituational)
        return {
          CardHeaderMiddle: CardHeaderMiddleDefault
        };
      if (isUnexpectedAppointment)
        return {
          Header: HeaderUnexpected,
          CardHeaderWrapper: NullComponent,
          CardHeaderLeft: CardHeaderLeftUnexpectedAppointment,
          CardHeaderMiddle: NullComponent,
          CardHeaderRight: NullComponent
        };
      if (isNonProtocolAndUnexpected)
        return {
          Header: isReview ? HeaderReview : NullComponent,
          CardLastSection: NullComponent
        };
      if (isReview) {
        return { Header: HeaderReview };
      }
      return {};
    },
    [isSituational, isUnexpectedAppointment, isNonProtocolAndUnexpected, isReview]
  );

  return (
    <EncounterSegment
      encounter={encounter}
      refetchTabData={refetchTabData}
      className={cx(
        `encounter-segment-${getEncounterTypeClassNameSuffix(encounterType, sitePatientEncounter, noAppointments)}`,
        { 'encounter-segment-canceled': cancelled }
      )}
      style={style}
      {...templates}
    />
  );
}

function getEncounterTypeClassNameSuffix(encounterType, sitePatientEncounter, noAppointments) {
  if (encounterType === UNEXPECTED && !sitePatientEncounter) {
    return 'unexpected-appointment';
  }
  if (noAppointments) {
    return 'no-appointments';
  }
  return encounterType.toLowerCase().replaceAll('_', '-');
}
